import axios from "axios";

export function initResellerWorkspace() {
  const root = document.querySelector(".rs-workspace");
  if (!root) return;
  const period = root.dataset.period;
  const companies = [...root.querySelectorAll(".rs-company")];
  const errorText = (error) =>
    error.response?.data?.message || "Forbindelsen mislykkedes. Prøv igen.";
  async function loadCompany(company) {
    if (company.dataset.loading || company.dataset.loaded) return;
    company.dataset.loading = "true";
    const panel = company.querySelector(".rs-company-panel");
    panel.setAttribute("aria-busy", "true");
    panel.textContent = "Henter brugere og aktivitet…";
    try {
      const response = await axios.get(
        `/api/v1/companies/${company.dataset.companyId}/workspace`,
        { params: { period } },
      );
      if (
        response.data.status !== "success" ||
        typeof response.data.html !== "string"
      )
        throw new Error("Invalid response");
      panel.innerHTML = response.data.html;
      company.dataset.loaded = "true";
    } catch (error) {
      panel.textContent = errorText(error);
      const retry = document.createElement("button");
      retry.type = "button";
      retry.className = "rs-button rs-button--small";
      retry.textContent = "Prøv igen";
      retry.addEventListener("click", () => loadCompany(company));
      panel.append(retry);
    } finally {
      delete company.dataset.loading;
      panel.removeAttribute("aria-busy");
    }
  }
  for (const company of companies)
    company.addEventListener("toggle", () => {
      if (company.open) loadCompany(company);
    });
  root.addEventListener("input", (event) => {
    const field = event.target;
    if (field.matches("[data-user-search]")) {
      const panel = field.closest(".rs-company-panel");
      let count = 0;
      for (const row of panel.querySelectorAll("[data-rs-user]")) {
        row.hidden = !row.dataset.search.includes(
          field.value.trim().toLowerCase(),
        );
        if (!row.hidden) count++;
      }
      panel.querySelector(".rs-user-empty").hidden = count > 0;
    }
    const settings = field.closest(".rs-settings-form");
    if (settings) settings.dataset.dirty = "true";
  });
  root.addEventListener("click", (event) => {
    if (!event.target.closest("[data-refresh-company]")) return;
    const company = event.target.closest(".rs-company");
    const form = company.querySelector(".rs-settings-form");
    if (form?.dataset.dirty) {
      form.querySelector(".rs-form-status").textContent =
        "Gem dine ændringer, før du opdaterer tallene.";
      return;
    }
    delete company.dataset.loaded;
    loadCompany(company);
  });
  root.addEventListener("submit", async (event) => {
    const form = event.target;
    if (form.matches(".rs-add-admin-form")) {
      event.preventDefault();
      const submit = form.querySelector("[type=submit]");
      if (submit.disabled) return;
      submit.disabled = true;
      const status = form.querySelector(".rs-form-status");
      status.textContent = "Opretter administrator…";
      try {
        await axios.post("/api/v1/users", {
          companyId: form.dataset.companyId,
          name: form.elements.adminName.value.trim(),
          email: form.elements.adminEmail.value.trim(),
          onboarding: true,
        });
        status.textContent =
          "Administratoren er oprettet. Opdatér tallene for at se brugeren i listen.";
        form.reset();
      } catch (error) {
        status.textContent = errorText(error);
      } finally {
        submit.disabled = false;
      }
      return;
    }
    if (!form.matches(".rs-settings-form")) return;
    event.preventDefault();
    const button = form.querySelector("[type=submit]");
    if (button.disabled) return;
    const status = form.querySelector(".rs-form-status");
    const data = {};
    for (const control of form.elements)
      if (control.name)
        data[control.name] =
          control.type === "checkbox" ? control.checked : control.value;
    button.disabled = true;
    status.textContent = "Gemmer indstillinger…";
    form.setAttribute("aria-busy", "true");
    try {
      const response = await axios.patch(
        `/api/v1/companies/${form.dataset.companyId}/workspace`,
        data,
      );
      if (response.data.status !== "success") throw new Error("Save failed");
      const company = form.closest(".rs-company");
      const previousActive = company.dataset.companyActive === "true";
      company.dataset.companyActive = String(response.data.data.active);
      const counter = root.querySelector("[data-active-company-count]");
      const activeCount =
        Number(root.dataset.activeCompanies) +
        Number(response.data.data.active) -
        Number(previousActive);
      root.dataset.activeCompanies = String(activeCount);
      if (counter) counter.textContent = `${activeCount} aktive`;
      const badge = company.querySelector("[data-company-status]");
      badge.textContent = response.data.data.active ? "Aktivt" : "Deaktiveret";
      badge.className = `rs-badge ${
        response.data.data.active ? "rs-badge--green" : "rs-badge--muted"
      }`;
      company.querySelector("[data-pbx-value]").textContent =
        response.data.data.pbxId || "Ikke tilsluttet";
      company.querySelector("[data-app-template]").textContent =
        response.data.data.appTemplate || "Ikke valgt";
      const integrations = company.querySelector(".rs-integrations");
      integrations.replaceChildren();
      for (const [key, label] of [
        ["hubspot", "HubSpot"],
        ["salesforce", "Salesforce"],
        [
          "tpIntegration",
          response.data.data.appTemplate === "aganalytics"
            ? "agAnalytics"
            : response.data.data.appTemplate === "egclinea"
              ? "EG-Clinea"
              : "Universal app",
        ],
        ["sms", "SMS"],
        ["dentalsuite", "DentalSuite"],
      ]) {
        if (data[key]) {
          const tag = document.createElement("span");
          tag.textContent = label;
          integrations.append(tag);
        }
      }
      delete form.dataset.dirty;
      if (form.elements.smsApiToken) form.elements.smsApiToken.value = "";
      if (form.elements.flexfoneApiKey) form.elements.flexfoneApiKey.value = "";
      status.textContent = "Indstillingerne er gemt.";
    } catch (error) {
      status.textContent = errorText(error);
    } finally {
      button.disabled = false;
      form.removeAttribute("aria-busy");
    }
  });
  const createPanel = root.querySelector("#rs-create");
  for (const link of root.querySelectorAll("[data-open-create]"))
    link.addEventListener("click", (event) => {
      event.preventDefault();
      createPanel.open = true;
      createPanel.scrollIntoView({ block: "start" });
      createPanel.querySelector("[name=name]").focus({ preventScroll: true });
    });
  const form = createPanel.querySelector("form");
  const button = form.querySelector("[type=submit]");
  const status = form.querySelector(".rs-form-status");
  const result = form.querySelector(".rs-create-result");
  const newKey = () => {
    if (window.crypto.randomUUID) return window.crypto.randomUUID();
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = [...bytes]
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16,
    )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
  let key = newKey();
  let created;
  let completed = false;
  const configure = () => {
    const admin = form.elements.createAdmin.checked;
    const pbx = form.elements.connectPbx.checked;
    const adminFields = form.querySelector("[data-admin-fields]");
    const pbxFields = form.querySelector("[data-pbx-fields]");
    adminFields.hidden = !admin;
    pbxFields.hidden = !pbx;
    for (const control of adminFields.querySelectorAll("input"))
      control.disabled = !admin;
    for (const control of pbxFields.querySelectorAll("input, select"))
      control.disabled = !pbx || !!created;
  };
  form.elements.createAdmin.addEventListener("change", configure);
  form.elements.connectPbx.addEventListener("change", configure);
  configure();
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (completed || button.disabled || !form.reportValidity()) return;
    button.disabled = true;
    form.setAttribute("aria-busy", "true");
    result.hidden = true;
    try {
      if (!created) {
        status.textContent = "Opretter firma…";
        const data = new FormData();
        for (const name of [
          "name",
          "email",
          "address",
          "zip",
          "city",
          "jwtCookieExpiresIn",
        ])
          data.append(name, form.elements[name].value.trim());
        data.append("creationKey", key);
        if (form.elements.connectPbx.checked)
          for (const name of ["pbxId", "flexfoneApiKey"])
            data.append(name, form.elements[name].value.trim());
        data.append(
          "recordings",
          JSON.stringify({
            keepRecordings: form.elements.connectPbx.checked
              ? form.elements.recordingMode.value
              : "no",
            deleteAfter: form.elements.retention.value,
          }),
        );
        if (form.elements.createAdmin.checked)
          for (const name of ["adminName", "adminEmail"])
            data.append(name, form.elements[name].value.trim());
        if (form.elements.logo.files[0])
          data.append("logo", form.elements.logo.files[0]);
        const response = await axios.post("/api/v1/companies", data);
        if (response.data.status !== "success" || !response.data.data?.id)
          throw new Error("Creation failed");
        created = response.data.data;
        for (const fieldset of form.querySelectorAll("[data-company-fields]"))
          fieldset.disabled = true;
        // Credentials need not remain in the document once the company exists.
        form.elements.flexfoneApiKey.value = "";
      }
      if (form.elements.createAdmin.checked) {
        status.textContent = `${created.name} er oprettet. Opretter administrator…`;
        const response = await axios.post("/api/v1/users", {
          name: form.elements.adminName.value.trim(),
          email: form.elements.adminEmail.value.trim(),
          companyId: created.id,
          onboarding: true,
        });
        if (response.data.status !== "success")
          throw new Error("Administrator creation failed");
      }
      status.textContent = form.elements.createAdmin.checked
        ? "Firma og administrator er oprettet. Administratoren kan bestille et loginlink på login-siden."
        : "Firmaet er oprettet. En administrator kan tilføjes senere.";
      result.replaceChildren();
      const link = document.createElement("a");
      link.className = "rs-button rs-button--primary";
      link.href = `/companies?open=${encodeURIComponent(created.id)}`;
      link.textContent = "Åbn firma i oversigten";
      result.append(link);
      const again = document.createElement("button");
      again.type = "button";
      again.className = "rs-button";
      again.textContent = "Opret endnu et firma";
      again.addEventListener("click", () => {
        form.reset();
        created = undefined;
        completed = false;
        key = newKey();
        for (const fieldset of form.querySelectorAll("[data-company-fields]"))
          fieldset.disabled = false;
        form.elements.createAdmin.disabled = false;
        button.hidden = false;
        button.textContent = "Opret firma";
        result.hidden = true;
        configure();
        status.textContent = "Firmaet tilknyttes automatisk din firmaoversigt.";
        form.elements.name.focus();
      });
      result.append(again);
      result.hidden = false;
      button.hidden = true;
      completed = true;
      form.elements.createAdmin.disabled = true;
    } catch (error) {
      status.textContent = created
        ? `${
            created.name
          } er allerede oprettet. Administratoren kunne ikke oprettes: ${errorText(
            error,
          )} Ret eventuelt administratorens oplysninger og prøv igen; firmaet oprettes ikke igen.`
        : errorText(error);
      button.textContent = created
        ? "Prøv administrator igen"
        : "Prøv oprettelse igen";
    } finally {
      button.disabled = false;
      form.removeAttribute("aria-busy");
    }
  });
  const openId = new URLSearchParams(window.location.search).get("open");
  const selected = companies.find(
    (company) => company.dataset.companyId === openId,
  );
  if (selected) {
    selected.open = true;
    selected.scrollIntoView({ block: "start" });
  }
  if (location.hash === "#rs-create") createPanel.open = true;
}
